import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import equal from 'fast-deep-equal';
import React from 'react';

import { ItemTitle } from '@/components/molecules/ItemTitle';
import { ListFormFieldField } from '@/components/molecules/ListFormFieldField';
import { Field } from '@/types/fields';
import { Identified } from '@/types/identified';
import { changeObserver } from '@/utility/changeObserver';
import { formLikeElementToObject } from '@/utility/formLikeElementToObject';

interface Props {
  readonly fields: readonly Field<any>[];
  readonly defaultItem: Identified<any>;
  readonly counter: number;
  readonly title?: string;

  onChange?(item: Identified<any>): void;
  onRemove(item: Identified<any>): void;
}

export const MultipleFieldsListFormFieldItem: React.FC<Props> = (
  props: Props,
): React.ReactElement => {
  const { counter, fields, defaultItem: item, title, onChange, onRemove } = props;
  const fieldsetRef = React.useRef<HTMLFieldSetElement>(null);

  const onRemoveWrapper = React.useCallback((): void => {
    onRemove(item);
  }, [item, onRemove]);

  const removeLabel = React.useMemo((): string => {
    const identifier = title?.replace('{{counter}}', String(counter)) ?? 'item';
    return `Remove ${identifier}`;
  }, [counter, title]);

  const updateValue = React.useCallback(
    (fieldset: HTMLFieldSetElement): void => {
      const newItem = { id: item.id, ...formLikeElementToObject(fieldset, fields) };
      if (!equal(item, newItem)) {
        return onChange?.(newItem);
      }
    },
    [fields, item, onChange],
  );

  React.useEffect((): VoidFunction | void => {
    const { current: fieldset } = fieldsetRef;
    if (fieldset === null) {
      return;
    }

    return changeObserver(fieldset, fields, updateValue);
  }, [fields, updateValue]);

  const renderField = React.useCallback(
    (field: Field<any>): React.ReactElement => (
      <ListFormFieldField key={String(field.name)} field={field} item={item} />
    ),
    [item],
  );

  return (
    <Box id={item.id}>
      <ItemTitle title={title} counter={counter} />
      <Box
        ref={fieldsetRef}
        component="fieldset"
        sx={styles.itemFieldset}
        data-skip="true"
        data-is-list-item={true}>
        {/* Got to have the id to make the item */}
        <input type="hidden" name="id" value={item.id} />

        {fields.map(renderField)}
      </Box>
      <Box sx={styles.removeLink}>
        <Box sx={styles.removeLinkLink} role="button" component="a" onClick={onRemoveWrapper}>
          <Typography
            fontSize={16}
            fontWeight={400}
            lineHeight="24px"
            color="error"
            sx={styles.removeLink}>
            {removeLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  removeLink: {
    display: 'block',
    textAlign: 'center',
    mt: 4,
    mx: 'auto',
  },
  removeLinkLink: {
    cursor: 'pointer',
  },
  itemFieldset: {
    display: 'flex',
    flexDirection: 'column',
    border: 'none',
    padding: 0,
    gap: 5,
  },
};
